/**
 * PDF Margin Parsing Utilities
 *
 * Utilities for parsing and validating PDF margin strings (e.g., "0.5in").
 */

import { DOCUMENT_MARGIN_UNITS, type DocumentMarginUnits, type Margin } from '@/reportEditor/report.constants';

const validDocumentMarginUnits = new Set(DOCUMENT_MARGIN_UNITS.map(({ shortLabel }) => shortLabel));

/**
 * Attempts to parse a page margin string (e.g., "0.5in") into a Margin object.
 * Returns undefined if parsing fails or the value is invalid.
 *
 * @param pageMargin - String representation of margin (e.g., "0.5in", "2cm")
 * @returns Parsed Margin object or undefined if invalid
 */
export function tryParsePageMargin(pageMargin: string): Margin | undefined {
  if (!pageMargin) return undefined;

  const numberMatch = pageMargin.match(/(\d+(\.\d+)?|\.\d+)/);
  const unitMatch = pageMargin.match(/[A-Za-z]+/);
  const unit = unitMatch?.[0] as DocumentMarginUnits | undefined;

  if (!numberMatch || !unit || !validDocumentMarginUnits.has(unit)) {
    return undefined;
  }

  const value = Number(numberMatch[0]);
  return Number.isFinite(value) ? { value, units: unit } : undefined;
}

/**
 * Parses a page margin string (e.g., "0.5in") into a Margin object.
 * Falls back to the provided fallback if parsing fails.
 *
 * @param pageMargin - String representation of margin
 * @param fallbackMargin - Margin to use if parsing fails
 * @returns Parsed Margin object or fallback
 */
export function parsePageMargin(pageMargin: string, fallbackMargin: Margin): Margin {
  return tryParsePageMargin(pageMargin) ?? fallbackMargin;
}
